import React, { useEffect, useState } from "react";
import { DollarSign, Send } from "lucide-react";
import axios from "axios";
import { FHOST } from "../constants/Functions.jsx";
import { authStorage } from "../../services/authStorage";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = authStorage.getAccessToken(); // ← changed
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // Fetch all transactions
      const transactionsRes = await axios.get(`${FHOST}/api/transactions/`, {
        headers,
      });
      const transactionsData = transactionsRes.data?.results || [];
      setTransactions(transactionsData);

      // Fetch all wallets
      const walletsRes = await axios.get(`${FHOST}/api/wallet/`, { headers });
      const walletsData = walletsRes.data?.results || [];
      setWallets(walletsData);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };
  fetchData();
}, []);

const fetchTransactionDetails = async (id) => {
  try {
    const token = authStorage.getAccessToken(); // ← changed
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    const response = await axios.get(`${FHOST}/api/transactions/${id}/`, {
      headers,
    });
    setSelectedTransaction(response.data);
    setShowTransactionModal(true);
  } catch (error) {
    console.error("Error fetching transaction details:", error);
  }
};

const handleUpdateTransaction = async (id, data) => {
  try {
    const token = authStorage.getAccessToken(); // ← changed
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    await axios.patch(`${FHOST}/api/transactions/${id}/`, data, { headers });
    // Refresh transactions
    const transactionsRes = await axios.get(`${FHOST}/api/transactions/`, {
      headers,
    });
    setTransactions(transactionsRes.data?.results || []);
    setShowTransactionModal(false);
  } catch (error) {
    console.error("Error updating transaction:", error);
  }
};

const handleDeleteTransaction = async (id) => {
  if (!window.confirm("Are you sure you want to delete this transaction?"))
    return;
  try {
    const token = authStorage.getAccessToken(); // ← changed
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    await axios.delete(`${FHOST}/api/transactions/${id}/`, { headers });
    // Refresh transactions
    const transactionsRes = await axios.get(`${FHOST}/api/transactions/`, {
      headers,
    });
    setTransactions(transactionsRes.data?.results || []);
  } catch (error) {
    console.error("Error deleting transaction:", error);
  }
};

  // Calculate Revenue, Payouts & Profit
  const totalRevenue = transactions
    .filter((t) => t.transaction_type === "deposit")
    .reduce((acc, t) => acc + parseFloat(t.amount || 0), 0);

  const totalPayouts = transactions
    .filter((t) => t.transaction_type === "withdrawal")
    .reduce((acc, t) => acc + parseFloat(t.amount || 0), 0);

  const totalProfit = totalRevenue - totalPayouts;
  const totalWalletBalance = wallets.reduce((acc, w) => acc + parseFloat(w.balance || 0), 0);


  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-full">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
              <p className="text-2xl font-semibold text-gray-900">{totalRevenue.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-red-100 p-3 rounded-full">
              <DollarSign className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Payouts</h3>
              <p className="text-2xl font-semibold text-gray-900">{totalPayouts.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-full">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Profit</h3>
              <p className="text-2xl font-semibold text-gray-900">{totalProfit.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-full">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Wallet Balance</h3>
              <p className="text-2xl font-semibold text-gray-900">{totalWalletBalance.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Transactions</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Payment Method
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td className="px-6 py-4" colSpan={7}>Loading...</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td className="px-6 py-4" colSpan={7}>No transactions found</td></tr>
            ) : transactions.map((transaction) => (
              <tr key={transaction.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(transaction.timestamp || transaction.created_at).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    transaction.transaction_type === "deposit" 
                      ? "bg-green-100 text-green-800" 
                      : transaction.transaction_type === "withdrawal"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                  }`}>
                    {transaction.transaction_type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {transaction.description || "N/A"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {transaction.amount_currency || ""} {parseFloat(transaction.amount || 0).toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {transaction.payment_method || "N/A"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      transaction.status === "completed" || transaction.status === "success"
                        ? "bg-green-100 text-green-800"
                        : transaction.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {transaction.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => fetchTransactionDetails(transaction.id)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleDeleteTransaction(transaction.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Transaction Detail Modal */}
      {showTransactionModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Transaction Details</h3>
              <button
                onClick={() => setShowTransactionModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Transaction ID</label>
                <p className="mt-1 text-sm text-gray-900">{selectedTransaction.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedTransaction.amount_currency || ""} {parseFloat(selectedTransaction.amount || 0).toFixed(2)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <p className="mt-1 text-sm text-gray-900">{selectedTransaction.transaction_type}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={selectedTransaction.status}
                  onChange={(e) => setSelectedTransaction({...selectedTransaction, status: e.target.value})}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateTransaction(selectedTransaction.id, { status: selectedTransaction.status })}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
